import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import API from '../../services/config/api';
import AuthLayout, { trackPointer, Field, AlertCircleIcon, ArrowRightIcon, MailIcon, LockIcon, EyeButton } from '../../Auth/AuthLayout';
import SsoLoginButton from '../../Auth/ssoAuth/SsoLoginButton';
import useSsoDiscovery from '../../Auth/ssoAuth/useSsoDiscovery';
import LoginOtpStep from '../../Auth/LoginOtpStep';

const AUDIT_MODULES = [
  { title: 'Workpapers',     code: 'WP',  tag: 'Digital audit docs',           icon: 'policy'     },
  { title: 'Evidence Trail', code: 'EVD', tag: 'Immutable audit log',           icon: 'audit'      },
  { title: 'KRI Tracking',   code: 'KRI', tag: 'Key risk indicators',           icon: 'score'      },
  { title: 'Audit Reports',  code: 'RPT', tag: 'One-click compliance reports',  icon: 'compliance' },
];

function InternalAuditLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');

  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState(null);

  const { sso, isChecking: ssoChecking } = useSsoDiscovery(form.email);

  const startSsoLogin = () => {
    sessionStorage.setItem('redirect_after_login', '/audit-portal');
    sessionStorage.setItem('audit_portal_sso', '1');
  };

  useEffect(() => {
    const portalToken = localStorage.getItem('audit_portal_token');
    const mainSession = sessionStorage.getItem('access_token');
    if (portalToken && mainSession) {
      navigate('/audit-portal');
    }
  }, [navigate]);

  if (inviteToken) {
    return <Navigate to={`/audit-portal/signup?token=${inviteToken}`} replace />;
  }

  const onChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const completeLogin = (authData) => {
    sessionStorage.setItem('access_token', authData.access_token);
    sessionStorage.setItem('refresh_token', authData.refresh_token);
    localStorage.setItem('user_profile', JSON.stringify(authData.user || {}));
    localStorage.setItem('audit_portal_token', authData.access_token);
    localStorage.setItem('audit_portal_refresh', authData.refresh_token);
    localStorage.setItem('audit_portal_user', JSON.stringify({
      email: authData.user?.email || authData.email || form.email,
      module: authData.user?.module || authData.module || 'audit',
      role: authData.user?.role || authData.role,
      first_name: authData.user?.first_name || authData.first_name,
      last_name: authData.user?.last_name || authData.last_name,
      organization_id: authData.user?.organization_id || authData.organization_id,
    }));
    navigate('/audit-portal');
  };

  const restartLogin = () => {
    setOtpChallenge(null);
    setError('');
  };

  const handleLogin = async () => {
    if (loading) return;
    if (!form.email || !form.password) { setError('Email and password are required.'); return; }
    setLoading(true); setError('');
    try {
      const axios = (await import('axios')).default;
      const authRes = await axios.post(
        API.AUDIT_PORTAL_LOGIN,
        { email: form.email, password: form.password },
        {
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
            'Authorization': 'Basic YmFzZWFkbWluOmRlZmF1bHRANDU2',
          },
        }
      );
      const authData = authRes?.data?.response_data?.data;
      if (authData?.otp_required) {
        setForm(prev => ({ ...prev, password: '' }));
        setOtpChallenge(authData);
        setLoading(false);
        return;
      }
      if (!authData?.access_token) {
        setError(authRes?.data?.error?.message || 'Invalid email or password.');
        setLoading(false);
        return;
      }
      completeLogin(authData);
    } catch (e) {
      const errMsg = e?.response?.data?.response_data?.message || e?.response?.data?.error?.message || '';
      setError(errMsg || 'Invalid email or password.');
    }
    setLoading(false);
  };

  const busy = loading || ssoChecking;

  return (
    <AuthLayout>
      <AuthLayout.Stage
        eyebrow="Audit management platform"
        headline="Audit smarter,"
        accent="not harder."
        modules={AUDIT_MODULES}
      />
      <AuthLayout.Panel>
        {otpChallenge ? (
          <LoginOtpStep challenge={otpChallenge} onVerified={completeLogin} onRestart={restartLogin}/>
        ) : (
        <div className="lg-wrap">
          <header className="lg-form-header">
            <p className="lg-eyebrow">
              <LockIcon/> Audit portal sign in
            </p>
            <h1 className="lg-title">Welcome back</h1>
            <p className="lg-lead">Enter your company email below to log in to your account.</p>
          </header>

          {error && (
            <div className="lg-alert lg-alert--error" role="alert">
              <AlertCircleIcon/> <span>{error}</span>
            </div>
          )}

          <form className="lg-form" noValidate onSubmit={e => { e.preventDefault(); handleLogin(); }}>
            <Field
              name="email"
              type="email"
              label="Work email"
              icon={<MailIcon/>}
              value={form.email}
              onChange={onChange}
              autoComplete="username"
              readOnly={loading}
            />

            {!sso.ssoEnabled && (
              <>
                <Field
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  label="Password"
                  icon={<LockIcon/>}
                  value={form.password}
                  onChange={onChange}
                  autoComplete="current-password"
                  readOnly={loading}
                  trailing={<EyeButton visible={showPassword} onToggle={() => setShowPassword(v => !v)}/>}
                />

                <div className="lg-row">
                  <span/>
                  <button type="button" className="lg-link" onClick={() => navigate('/audit-portal/forgot-password')}>
                    Forgot password?
                  </button>
                </div>

                <button type="submit" className="lg-submit lg-spot"
                  disabled={busy} aria-busy={loading} onPointerMove={trackPointer}>
                  {loading ? (
                    <span className="lg-submit-label"><span className="lg-spin" aria-hidden="true"/> Signing in...</span>
                  ) : ssoChecking ? (
                    <span className="lg-submit-label"><span className="lg-spin" aria-hidden="true"/> Checking...</span>
                  ) : (
                    <span className="lg-submit-label">Sign in <ArrowRightIcon/></span>
                  )}
                  {loading && <i className="lg-scan" aria-hidden="true"/>}
                </button>
              </>
            )}

            <SsoLoginButton
              sso={sso}
              isChecking={ssoChecking}
              disabled={loading}
              onBeforeNavigate={startSsoLogin}
            />
          </form>
        </div>
        )}

        <p className="lg-trust">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="4.5" y="10.5" width="15" height="10" rx="3"/>
            <path d="M8 10.5V8a4 4 0 018 0v2.5"/>
          </svg>
          Protected by enterprise-grade security. Foresee Security - Internal Audit Portal
        </p>
      </AuthLayout.Panel>
    </AuthLayout>
  );
}

export default InternalAuditLogin;
